import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Calendar, Users, CheckCircle2, Clock, Wallet, Search } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Aurora } from "@/components/motion/Aurora";

const STATUS_LABEL = {
  pending: { label: "Pendiente", cls: "bg-warn/15 text-warn-ink" },
  active: { label: "En curso", cls: "bg-forest-deep/10 text-forest-deep" },
  completed: { label: "Completada", cls: "bg-ok/15 text-forest" },
};

function StatCard({ icon, label, value, color = "#22381f" }) {
  return (
    <div className="bg-white border border-sand rounded-xl p-5 hover:shadow-md transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <span className="text-overline">{label}</span>
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15`, color }}
        >
          {icon}
        </div>
      </div>
      <div className="font-display text-3xl font-bold tabular-nums text-charcoal">
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/reservations"),
      ]);
      setStats(s.data);
      setReservations(r.data);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = reservations.filter(
    (r) =>
      r.client_name.toLowerCase().includes(search.toLowerCase()) ||
      r.code.toLowerCase().includes(search.toLowerCase()),
  );

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="aurora-page relative isolate min-h-screen bg-ivory">
      <Aurora variant="subtle" fixed />
      <header className="bg-white border-b border-sand sticky top-0 z-30">
        <div className="container-x flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-forest-deep text-white flex items-center justify-center font-display font-bold">
              V
            </div>
            <div>
              <div className="font-display font-bold text-charcoal">VanCheck Admin</div>
              <div className="text-xs text-warmgray-light">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => window.open("https://campervan.cat/admin", "_blank")}
              data-testid="admin-web-btn"
            >
              Web ↗
            </Button>
            <Button
              variant="ghost"
              onClick={() => window.open("https://stats.campervan.cat/campervan.cat", "_blank")}
              data-testid="admin-stats-btn"
            >
              Stats ↗
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/productos")}
              data-testid="admin-products-btn"
            >
              Productos
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/finanzas")}
              data-testid="admin-finance-btn"
            >
              Finanzas
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/checklist")}
              data-testid="admin-checklist-btn"
            >
              Checklist devolución
            </Button>
            <Button
              onClick={() => navigate("/admin/reservations/new")}
              className="bg-terracotta hover:bg-terracotta-dark text-white"
              data-testid="admin-new-reservation-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Nueva reserva
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        <div className="mb-8">
          <span className="text-overline">Panel</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-charcoal mt-2">
            Resumen de la flota
          </h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard icon={<Users className="h-4 w-4" />} label="Total reservas" value={stats?.total ?? "·"} />
          <StatCard icon={<Clock className="h-4 w-4" />} label="Pendientes" value={stats?.pending ?? "·"} color="#E89B33" />
          <StatCard icon={<Calendar className="h-4 w-4" />} label="En curso" value={stats?.active ?? "·"} />
          <StatCard icon={<CheckCircle2 className="h-4 w-4" />} label="Completadas" value={stats?.completed ?? "·"} color="#4A7C59" />
          <StatCard icon={<Wallet className="h-4 w-4" />} label="Fianzas activas" value={stats?.deposits_held ?? "·"} color="#c4704f" />
        </div>

        <div className="bg-white border border-sand rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-sand flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <h2 className="font-display text-xl font-semibold text-charcoal">
              Reservas
            </h2>
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-warmgray-light" />
              <Input
                placeholder="Buscar por nombre o código"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-ivory border-sand"
                data-testid="admin-search-input"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center text-warmgray-light">
              {reservations.length === 0
                ? "Aún no hay reservas. Crea la primera para empezar."
                : "Sin resultados."}
            </div>
          ) : (
            <ul className="divide-y divide-sand">
              {filtered.map((r) => {
                const st = STATUS_LABEL[r.status] || STATUS_LABEL.pending;
                return (
                  <li key={r.id}>
                    <Link
                      to={`/admin/reservations/${r.id}`}
                      className="flex items-center justify-between px-5 py-4 hover:bg-ivory transition-colors"
                      data-testid={`admin-reservation-row-${r.code}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="font-mono text-sm font-semibold text-forest-deep bg-sand-light px-2.5 py-1 rounded-md">
                          {r.code}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-charcoal truncate">
                            {r.client_name}
                          </div>
                          <div className="text-xs text-warmgray-light">
                            {r.pickup_date} · {r.pickup_time} → {r.return_date} · {r.return_time}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {r.payment_status === "paid" && (
                          <Badge className="bg-ok/15 text-forest hover:bg-ok/15 border-0">
                            Fianza pagada
                          </Badge>
                        )}
                        {r.payment_status === "refunded" && (
                          <Badge className="bg-warmgray/15 text-warmgray hover:bg-warmgray/15 border-0">
                            Fianza devuelta
                          </Badge>
                        )}
                        <Badge className={`${st.cls} hover:${st.cls} border-0`}>
                          {st.label}
                        </Badge>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
